import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@tabipla/ui";
import { getAuthErrorMessage } from "@tabipla/shared";

export interface AuthPageTheme {
  /** アイコン背景色 (例: "bg-emerald-500") */
  iconBg: string;
  /** アイコンテキスト色 (例: "text-white") */
  iconText: string;
  /** ボタンのクラス (例: "bg-emerald-500 text-white shadow-lg hover:bg-emerald-600") */
  buttonClass: string;
  /** 背景グラデーションクラス (例: "from-white via-gray-50 to-emerald-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-emerald-950/30") */
  backgroundGradient: string;
}

export interface AuthPageProps {
  /** アプリ名 */
  appName: string;
  /** タグライン */
  tagline: string;
  /** アイコン (ReactNode) */
  icon: React.ReactNode;
  /** テーマ設定 */
  theme: AuthPageTheme;
  /** モード: "login" または "register" */
  mode: "login" | "register";
  /** ログイン処理 */
  onLogin?: (email: string, password: string) => Promise<void>;
  /** 登録処理 */
  onRegister?: (email: string, password: string, name: string) => Promise<void>;
  /** LINEログイン処理 */
  onLoginWithLine?: () => Promise<void>;
  /** LINEログインボタンを表示するか */
  showLineLogin?: boolean;
  /** ローディング状態 */
  isLoading: boolean;
}

export function AuthPage({
  appName,
  tagline,
  icon,
  theme,
  mode,
  onLogin,
  onRegister,
  onLoginWithLine,
  showLineLogin = true,
  isLoading,
}: AuthPageProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isLogin = mode === "login";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isLogin) {
      if (password !== confirmPassword) {
        setError(t("auth.passwordsDoNotMatch"));
        return;
      }

      if (password.length < 8) {
        setError(t("auth.passwordTooShort"));
        return;
      }
    }

    try {
      if (isLogin) {
        await onLogin?.(email, password);
      } else {
        await onRegister?.(email, password, name);
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err instanceof Error ? err : null, t, isLogin);
      setError(errorMessage);
    }
  };

  return (
    <div
      className={`flex min-h-screen items-center justify-center bg-gradient-to-b p-4 ${theme.backgroundGradient}`}
    >
      <div className="animate-scale-in w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg ${theme.iconBg} ${theme.iconText}`}
          >
            {icon}
          </div>
          <h1 className="text-2xl font-bold">{appName}</h1>
          <p className="text-sm text-muted-foreground">{tagline}</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-xl">
              {isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}
            </CardTitle>
            <CardDescription>
              {isLogin ? t("auth.loginDescription") : t("auth.registerDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {!isLogin && (
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    {t("auth.name")}
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={t("auth.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("auth.email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  {t("auth.password")}
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder={
                    isLogin ? t("auth.passwordPlaceholder") : t("auth.passwordMinLength")
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="text-sm font-medium">
                    {t("auth.confirmPassword")}
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder={t("auth.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
              )}

              <Button
                type="submit"
                className={`w-full ${theme.buttonClass}`}
                size="lg"
                disabled={isLoading}
              >
                {isLoading
                  ? isLogin
                    ? t("common.loading")
                    : t("auth.creatingAccount")
                  : isLogin
                    ? t("auth.login")
                    : t("auth.registerTitle")}
              </Button>

              {/* LINE Login */}
              {showLineLogin && onLoginWithLine && (
                <>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-card px-2 text-muted-foreground">
                        {t("auth.orContinueWith", { defaultValue: "または" })}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#06C755] bg-[#06C755] text-white hover:bg-[#05B34D] hover:text-white"
                    size="lg"
                    onClick={onLoginWithLine}
                    disabled={isLoading}
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                    </svg>
                    {t("auth.continueWithLine", { defaultValue: "LINEで続ける" })}
                  </Button>

                  {/* LINE ログインの案内文 */}
                  {isLogin && (
                    <p className="text-center text-xs text-muted-foreground">
                      {t("auth.lineLoginNote", {
                        defaultValue:
                          "※既存アカウントにLINEを連携する場合は、先にログインしてから設定画面で連携してください",
                      })}
                    </p>
                  )}
                </>
              )}

              <p className="text-center text-sm">
                <span className="text-muted-foreground">
                  {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}
                </span>{" "}
                <Link
                  to={isLogin ? "/register" : "/login"}
                  className="font-semibold text-primary underline hover:opacity-80"
                >
                  {isLogin ? t("auth.register") : t("auth.login")}
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-muted-foreground">{t("auth.termsNotice")}</p>
      </div>
    </div>
  );
}
